import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Lock, LockOpen, Trash2, Star, GripVertical } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import * as frentesApi from '@/api/frentes.api';
import type { Frente } from '@/api/frentes.api';

/**
 * TUS FRENTES — cómo divides tu vida.
 *
 * Esteban: *"si quiero agregar otro, que yo tenga la posibilidad de agregar
 * otro... ningún frente es más importante que otro."*
 *
 * ═══ LO QUE ESTA PANTALLA NO TIENE, A PROPÓSITO ═══
 *
 * No hay "frente principal", ni estrella de favorito, ni orden por importancia.
 * `orden` es en qué fila se dibuja y se cambia con las flechas — nada más. El
 * único distintivo es el **predeterminado**, y no significa "el más
 * importante": significa "donde cae lo que Athena no supo clasificar".
 *
 * ═══ BORRAR UN FRENTE NO BORRA LO QUE HABÍA DENTRO ═══
 *
 * Los asuntos quedan sin frente y los chats vuelven a "Sin clasificar". Borrar
 * una carpeta no puede llevarse lo que estaba adentro: eso es perder trabajo
 * por una decisión de orden.
 */

const COLORES = [
  '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b',
  '#ef4444', '#06b6d4', '#ec4899', '#64748b',
];

function FilaDeFrente({
  f,
  onEditar,
  onBorrar,
  onPorDefecto,
  ocupado,
}: {
  f: Frente;
  onEditar: (id: string, cambios: Partial<Frente>) => void;
  onBorrar: (f: Frente) => void;
  onPorDefecto: (id: string) => void;
  ocupado: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const [nombre, setNombre] = useState(f.nombre);

  return (
    <div className="flex flex-col gap-2 border-b border-slate-100 py-3 last:border-0 dark:border-slate-800">
      {/* EL NOMBRE, EN SU PROPIA FILA.
          En 390 px, el nombre + el chip "Predeterminado" + los contadores no
          entran juntos: el nombre se cortaba en "Traba". Ahora el nombre manda
          la fila y lo demás baja. */}
      <div className="flex items-center gap-2.5">
        <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />
        <span className="h-4 w-4 shrink-0 rounded-full" style={{ backgroundColor: f.color }} />

        {editando ? (
          <input
            autoFocus
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onBlur={() => {
              setEditando(false);
              if (nombre.trim() && nombre !== f.nombre) onEditar(f.id, { nombre: nombre.trim() });
              else setNombre(f.nombre);
            }}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            className="min-h-[36px] flex-1 rounded border border-slate-300 px-2 text-sm dark:border-slate-600 dark:bg-slate-900"
          />
        ) : (
          <button
            onClick={() => setEditando(true)}
            className="min-w-0 flex-1 truncate text-left text-base font-medium text-slate-900 dark:text-slate-100"
          >
            {f.nombre}
          </button>
        )}

        {f.esPorDefecto && (
          <Badge className="shrink-0 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Por defecto
          </Badge>
        )}
      </div>

      <p className="pl-10 text-xs text-slate-400">
        {f.chats ?? 0} {f.chats === 1 ? 'chat' : 'chats'} · {f.asuntos ?? 0}{' '}
        {f.asuntos === 1 ? 'asunto' : 'asuntos'}
      </p>

      <div className="flex flex-wrap items-center gap-1.5 pl-10">
        {COLORES.map((c) => (
          <button
            key={c}
            onClick={() => onEditar(f.id, { color: c })}
            aria-label={`Color ${c}`}
            className={cn(
              'h-6 w-6 rounded-full transition',
              f.color === c && 'ring-2 ring-slate-400 ring-offset-2 dark:ring-offset-slate-900',
            )}
            style={{ backgroundColor: c }}
          />
        ))}

        <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />

        <button
          onClick={() => onEditar(f.id, { conCandado: !f.conCandado })}
          disabled={ocupado}
          className={cn(
            'flex min-h-[36px] items-center gap-1 rounded-md px-2 text-xs',
            f.conCandado
              ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
              : 'text-slate-500',
          )}
        >
          {f.conCandado ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
          {f.conCandado ? 'Con candado' : 'Sin candado'}
        </button>

        {!f.esPorDefecto && (
          <>
            <button
              onClick={() => onPorDefecto(f.id)}
              disabled={ocupado}
              className="flex min-h-[36px] items-center gap-1 rounded-md px-2 text-xs text-slate-500"
            >
              <Star className="h-3.5 w-3.5" /> Predeterminado
            </button>
            <button
              onClick={() => onBorrar(f)}
              disabled={ocupado}
              className="flex min-h-[36px] items-center gap-1 rounded-md px-2 text-xs text-red-600 dark:text-red-400"
            >
              <Trash2 className="h-3.5 w-3.5" /> Borrar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Frentes() {
  const navegar = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [nuevo, setNuevo] = useState('');

  const frentes = useQuery({ queryKey: ['frentes'], queryFn: frentesApi.getFrentes });

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ['frentes'] });
    void qc.invalidateQueries({ queryKey: ['chats'] });
  };

  const crear = useMutation({
    mutationFn: () => frentesApi.crearFrente({ nombre: nuevo.trim() }),
    onSuccess: (f) => {
      toast.success(`"${f.nombre}" creado`);
      setNuevo('');
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const editar = useMutation({
    mutationFn: ({ id, cambios }: { id: string; cambios: Partial<Frente> }) =>
      frentesApi.editarFrente(id, cambios),
    onSuccess: refrescar,
    onError: (e: Error) => toast.error(e.message),
  });

  const borrar = useMutation({
    mutationFn: (id: string) => frentesApi.borrarFrente(id),
    onSuccess: () => {
      toast.success('Frente borrado. Lo que tenía adentro quedó sin clasificar.');
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const porDefecto = useMutation({
    mutationFn: (id: string) => frentesApi.marcarPorDefecto(id),
    onSuccess: () => {
      toast.success('Ahí caerá lo que Athena no sepa clasificar.');
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const ocupado = editar.isPending || borrar.isPending || porDefecto.isPending;
  const lista = frentes.data?.frentes ?? [];

  return (
    <div className="space-y-3">
      <button
        onClick={() => navegar(-1)}
        className="flex min-h-[44px] items-center gap-1 text-sm text-slate-600 dark:text-slate-300"
      >
        <ArrowLeft className="h-4 w-4" /> Atrás
      </button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Tus frentes</h1>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          Como divides tu vida. Ninguno vale más que otro: el orden es solo para verlos.
        </p>
      </div>

      <Card>
        <CardBody className="flex gap-2">
          <input
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && nuevo.trim() && crear.mutate()}
            placeholder="Familia, Mute Monkey, La casa…"
            className="min-h-[44px] flex-1 rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
          <Button onClick={() => crear.mutate()} disabled={!nuevo.trim() || crear.isPending}>
            <Plus className="h-4 w-4" /> Agregar
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-0">
          {frentes.isPending ? (
            <Spinner />
          ) : lista.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">
              Todavía no tienes frentes. Crea el primero arriba.
            </p>
          ) : (
            lista.map((f) => (
              <FilaDeFrente
                key={f.id}
                f={f}
                ocupado={ocupado}
                onEditar={(id, cambios) => editar.mutate({ id, cambios })}
                onBorrar={(x) => borrar.mutate(x.id)}
                onPorDefecto={(id) => porDefecto.mutate(id)}
              />
            ))
          )}
        </CardBody>
      </Card>

      <p className="px-1 text-xs text-slate-400">
        Borrar un frente no borra lo que tenía adentro: los chats vuelven a "Sin clasificar" y
        los asuntos quedan sin frente.
      </p>
    </div>
  );
}
